import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getRecentChats, hasMoreChats, resetChatsPagination } from '../api/apiApi';
import { setChatsList, setCurrentChatId } from '../store/chatSlice';
import { setToggleMenuShown } from '../store/uiSlice';
import { getInputElement } from "@components/chatComponent";
export const ChatsHistoryComponent = () => {
    const dispatch = useDispatch();
    const chatsList = useSelector((state) => state.chat.chatsList);
    const currentChatId = useSelector((state) => state.chat.currentChatId);
    const toggleMenuShownState = useSelector((state) => state.ui.toggleMenuShown);
    const [loadingMessage, setLoadingMessage] = useState(null);
    const [chatsListMockup, setChatsListMockup] = useState([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const historyRef = useRef(null);
    const containerRef = useRef(null);
    const loadRecentChats = useCallback(async (isInitial = false) => {
        if (isInitial) {
            setLoadingMessage('Loading...');
            setIsInitialLoad(true);
        }
        else {
            if (!hasMoreChats() || isLoadingMore)
                return;
            setIsLoadingMore(true);
        }
        const chats = await getRecentChats();
        if (!chats.result) {
            if (isInitial) {
                setLoadingMessage(`Error: ${chats.errorMessage}`);
            }
            setIsLoadingMore(false);
            setIsInitialLoad(false);
            return;
        }
        if (isInitial) {
            setLoadingMessage(null);
            setIsInitialLoad(false);
        }
        else {
            setIsLoadingMore(false);
        }
        if (chats.data) {
            const currentChatsList = isInitial ? [] : chatsList;
            let loadedChats = [...currentChatsList];
            loadedChats.push(...chats.data);
            dispatch(setChatsList(loadedChats));
        }
    }, [chatsList, isLoadingMore, dispatch]);
    useEffect(() => {
        resetChatsPagination();
        const loadInitial = async () => {
            setLoadingMessage('Loading...');
            setIsInitialLoad(true);
            const chats = await getRecentChats();
            if (!chats.result) {
                setLoadingMessage(`Error: ${chats.errorMessage}`);
                setIsInitialLoad(false);
                return;
            }
            setLoadingMessage(null);
            setIsInitialLoad(false);
            if (chats.data) {
                dispatch(setChatsList(chats.data));
            }
        };
        loadInitial();
    }, [dispatch]);
    async function handleNewChat() {
        dispatch(setCurrentChatId(null));
        getInputElement()?.focus();
    }
    useEffect(() => {
        if (chatsList.length > 0) {
            renderHistory(chatsList);
        }
    }, [chatsList, currentChatId]);
    function renderHistory(chats) {
        setLoadingMessage('');
        const chatsListMockups = [];
        chats.forEach((chat) => {
            chatsListMockups.push(_jsxs("div", { className: `chat-item chat-list ${chat.chatId === currentChatId ? 'active-chat' : ''}`, onClick: () => {
                    dispatch(setCurrentChatId(chat.chatId));
                }, children: [chat.chatName, " ", _jsx("span", { className: "chatTime", title: new Date(chat.lastActiveAt).toLocaleString(), children: _jsx("svg", { viewBox: "0 0 100 100", children: _jsx("use", { xlinkHref: "#calendarThumb" }) }) })] }, chat.chatId));
        });
        setChatsListMockup(chatsListMockups);
    }
    // Auto-load more chats if scroll is not available
    useLayoutEffect(() => {
        if (isInitialLoad || isLoadingMore || !containerRef.current)
            return;
        const container = containerRef.current;
        if (!container)
            return;
        const hasScroll = container.scrollHeight > container.clientHeight;
        // If no scroll and more chats available, load more
        if (!hasScroll && hasMoreChats() && !isLoadingMore) {
            loadRecentChats(false);
        }
    }, [chatsListMockup, isInitialLoad, isLoadingMore, loadRecentChats]);
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        const handleScroll = () => {
            const scrollTop = container.scrollTop;
            const scrollHeight = container.scrollHeight;
            const clientHeight = container.clientHeight;
            const scrollBottom = scrollHeight - scrollTop - clientHeight;
            // Load more when scrolled within 100px of bottom
            if (scrollBottom < 100 && hasMoreChats() && !isLoadingMore && !isInitialLoad) {
                loadRecentChats(false);
            }
        };
        container.addEventListener('scroll', handleScroll);
        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [isLoadingMore, isInitialLoad, loadRecentChats]);
    useLayoutEffect(() => {
        const update = () => {
            const w = historyRef.current.offsetWidth;
            document.body.style.paddingLeft = `${w}px`;
        };
        update();
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('resize', update);
        };
    }, [toggleMenuShownState]);
    return (_jsx("div", { id: "chat-history", ref: historyRef, className: toggleMenuShownState ? 'show' : 'hide', "aria-hidden": !toggleMenuShownState, children: _jsxs("div", { className: "chatHistoryContainer", ref: containerRef, children: [_jsxs("div", { className: "chatHistoryHeader", children: [_jsxs("div", { className: `chat-item`, role: "button", title: toggleMenuShownState ? 'Close chat history' : 'Show chat history', onClick: () => {
                                dispatch(setToggleMenuShown(!toggleMenuShownState));
                            }, children: [_jsx("svg", { viewBox: "0 0 100 100", width: "18", height: "18", children: _jsx("use", { xlinkHref: toggleMenuShownState ? "#hideChatsThumb" : "#showChatsThumb" }) }), _jsx("span", { children: "Close chat history" })] }), _jsxs("div", { className: `chat-item newChatButton`, role: "button", tabIndex: 0, title: "New chat", onClick: handleNewChat, onKeyDown: (e) => {
                                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                                    e.preventDefault();
                                }
                            }, children: [_jsx("svg", { viewBox: "0 0 100 100", width: "18", height: "18", children: _jsx("use", { xlinkHref: "#editThumb" }) }), _jsx("span", { children: "New chat" })] }, 'newChat'), _jsx("hr", { className: "marginHr" })] }), loadingMessage && _jsx("div", { className: "loader loadingMessage", children: loadingMessage }), !loadingMessage &&
                    _jsxs(_Fragment, { children: [_jsx("p", { className: "chats-label", children: "Chats" }), chatsListMockup, isLoadingMore && hasMoreChats() && (_jsx("div", { className: "loadMoreLoader", children: _jsxs("div", { className: "loadingDots", children: [_jsx("i", {}), _jsx("i", {}), _jsx("i", {})] }) }))] })] }) }));
};
